import { useQuery } from '@apollo/client/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetAllCards } from '../../../hooks/apolloHooks/useGetAllCards';
import { useGetAllSites } from '../../../hooks/apolloHooks/useGetAllSites';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { useGetProducts } from '../../../hooks/apolloHooks/useGetProducts';
import { GET_BANKCARDS_CUSTOMER_DATA_QUERY, GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import type { BankCardsCustomerDataResponse, Transaction, TransactionFilterInput, TransactionsFullCustomerDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { SearchCrossIcon } from '../../svgs/ExpressonSVGs';
import type { CardTypeOption } from '../../views/Transactions/TransactionsTable';
import FetchingAnimation from '../FetchingAnimation';
import type { CardOption } from './TransactionFilter';
import TransactionTableSingleRow from './TransactionTableSingleRow';


type TransactionTableListProps = {
    isCompany?: boolean;
    selectedCardType?: CardTypeOption | null;
    selectedCard?: CardOption | null;
    startDate?: Date | null;
    endDate?: Date | null;
}

const TransactionTableList: React.FC<TransactionTableListProps> = ({
    isCompany = false,
    selectedCardType = null,
    selectedCard = null,
    startDate = null,
    endDate = null
}) => {
    // Fetch all regular cards to create lookup map for maskedPan
    const { cards, loading: cardsLoading } = useGetAllCards();

    // Fetch bank cards
    const { data: bankCardsData, loading: bankCardsLoading } = useQuery<BankCardsCustomerDataResponse>(
        GET_BANKCARDS_CUSTOMER_DATA_QUERY,
        { fetchPolicy: 'cache-first' }
    );

    // In component:
    const { sites, loading: sitesLoading } = useGetAllSites();

    // Create lookup map: siteId → site name
    const siteLookup = useMemo(() => {
        const map: Record<number, string> = {};

        if (sitesLoading) {
            // Return a special marker indicating sites are loading
            return { __loading: true } as any;
        }

        sites.forEach(site => {
            map[site.id] = site.name;  // or site.shortName
        });

        return map;
    }, [sites, sitesLoading]);

    // Fetch all products (fuel types, etc.)
    const { products, loading: productsLoading } = useGetProducts();

    const productLookup = useMemo(() => {
        const map: Record<number | string, string> = {};
        products.forEach(product => {
            map[product.id] = product.name;
        })
        return map;
    }, [products]);

    // Create lookup map: cardNumber → maskedPan (for regular cards)
    const cardLookupMaskedPan = useMemo(() => {
        const map: Record<number | string, string> = {};

        // Add regular cards (keyed by card.number)
        cards.forEach(card => {
            map[card.number] = card.maskedPan || '••••';
        });

        return map;
    }, [cards]);

    // Prefer the user-defined card name, then fall back to the generated card reference.
    const cardLookupRef = useMemo(() => {
        const map: Record<number | string, string | null> = {};

        cards.forEach(card => {
            map[card.number] = card.reference2 || card.reference1 || null;
        });

        return map;
    }, [cards]);

    // Create lookup map: cardNumber → isDisabled
    const cardLookupDisabled = useMemo(() => {
        const map: Record<number | string, boolean> = {};

        cards.forEach(card => {
            map[card.number] = card.limit?.disabled?.isDisabled === true;
        });

        return map;
    }, [cards]);


    // Get the first bank card's maskedPan (bank card transactions have cardNumber: null)
    const bankCardMaskedPan = useMemo(() => {
        const bankCards = bankCardsData?.currentCustomer?.bankCards?.nodes;
        if (bankCards && bankCards.length === 1) {
            return bankCards[0].maskedPan;
        }
        return undefined;
    }, [bankCardsData]);

    // Helper function to get site name for a transaction
    const getSiteName = (siteId: number): string => {
        // Check if sites are still loading
        if (siteLookup.__loading) {
            return <FetchingAnimation width={200} height={25} /> as any; // Return loading animation component
        }
        // Return site name if found, otherwise default message
        return siteLookup[siteId] || 'Plats saknas för tillfället';
    };
    const getMaskedPan = (transaction: Transaction): string | undefined => {
        // Bank card transactions have cardNumber: null, use first bank card
        if (transaction.transactionType === 'B') {
            //return bankCardMaskedPan; TODO: here we nee to pass the correct bank card maskedPan based on transactions details.
            return ''
        }
        // Regular cards use cardNumber lookup
        if (transaction.cardNumber) {
            return cardLookupMaskedPan[transaction.cardNumber];
        }
        return undefined;
    };

    // Helper function to get card reference 1 for a transaction
    const getCardRef = (transaction: Transaction): string | null | undefined => {
        // Bank card transactions have cardNumber: null, no reference available
        if (transaction.transactionType === 'B') {
            return null;
        }
        // Regular cards use cardNumber lookup
        if (transaction.cardNumber) {
            return cardLookupRef[transaction.cardNumber];
        }
        return undefined;
    }

    // Helper function to check if card is disabled for a transaction
    const getIsCardDisabled = (transaction: Transaction): boolean => {
        // Bank cards don't have disabled status in our lookup
        if (transaction.transactionType === 'B') {
            return false;
        }
        // Regular cards use cardNumber lookup
        if (transaction.cardNumber) {
            return cardLookupDisabled[transaction.cardNumber] || false;
        }
        return false;
    }

    /**
     * All STATES,LOGIC and variables for fetching paginated transactions
     */
    // State to accumulate all fetched transactions
    const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
    // Cursor for pagination - null means fetch from the end (most recent)
    const [currentBefore, setCurrentBefore] = useState<string | null>(null);
    // Store the startCursor for "Load More" functionality
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    // Track if there are more pages to load from the API
    const [hasMorePages, setHasMorePages] = useState(true);
    // Track if we're doing batch loading (initial or load more)
    const [isBatchLoading, setIsBatchLoading] = useState(true);
    // Track if this is the very first load (no data yet)
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    // Track if this load was triggered by "Load More" button (for scroll behavior)
    const [isLoadMoreTriggered, setIsLoadMoreTriggered] = useState(false);
    // Track last processed cursor to avoid duplicate processing
    const [lastProcessedCursor, setLastProcessedCursor] = useState<string | null>(null);
    // Track newly loaded transaction IDs for animation
    const [newlyLoadedIds, setNewlyLoadedIds] = useState<Set<string>>(new Set());
    // Target number of transactions for initial load
    const TARGET_INITIAL_COUNT = 20;
    // Batch size per request (API limit)
    const BATCH_SIZE = 10;

    // Track previous filters to detect changes
    const prevFilterRef = useRef<{ cardType: CardTypeOption | null; card: CardOption | null; startDate: Date | null; endDate: Date | null }>({
        cardType: null,
        card: null,
        startDate: null,
        endDate: null
    });

    // Helper: Check if transaction matches selected card
    const matchesSelectedCard = (transaction: Transaction): boolean => {
        if (!selectedCard) return true;  // No filter = match all

        // For bank cards, match by transaction type (cardNumber is null for both)
        if (selectedCard.type === 'bankcard') {
            return transaction.transactionType === 'B';
        }

        // Determine expected transaction type
        const expectedTransactionType = selectedCard.type === 'smartcard' ? 'P' : 'K';

        // Must match transaction type
        if (transaction.transactionType !== expectedTransactionType) {
            return false;
        }

        // If transaction has cardNumber, match it with selected card's cardNumber
        // Note: API sometimes returns cardNumber: null for smartcard transactions (type P)
        // In that case, we can only filter by type (already done above via server-side filter)
        if (transaction.cardNumber !== null) {
            return transaction.cardNumber === selectedCard.cardNumber;
        }

        // cardNumber is null - for smartcards this means we can't filter by specific card
        // Show the transaction anyway (user selected this card type via specific card selection)
        // TODO: Investigate why API returns null cardNumber for smartcard transactions
        return true;
    };

    // Filter transactions based on specific card (client-side, card type is now server-side)
    const filteredTransactions = useMemo(() => {
        if (!selectedCard) return allTransactions;
        return allTransactions.filter(t => matchesSelectedCard(t));
    }, [allTransactions, selectedCard]);

    // Reset transactions when filter changes (card type, specific card, or date range)
    useEffect(() => {
        const cardTypeChanged = prevFilterRef.current.cardType?.value !== selectedCardType?.value;
        const cardChanged = prevFilterRef.current.card?.value !== selectedCard?.value;
        // Compare dates by time value (not object reference)
        const startDateChanged = prevFilterRef.current.startDate?.getTime() !== startDate?.getTime();
        const endDateChanged = prevFilterRef.current.endDate?.getTime() !== endDate?.getTime();

        if (cardTypeChanged || cardChanged || startDateChanged || endDateChanged) {
            // Filter changed - reset state to re-fetch with new filter context
            prevFilterRef.current = {
                cardType: selectedCardType,
                card: selectedCard,
                startDate: startDate,
                endDate: endDate
            };
            setAllTransactions([]);
            setCurrentBefore(null);
            setNextCursor(null);
            setHasMorePages(true);
            setIsBatchLoading(true);
            setIsFirstLoad(true);
            setLastProcessedCursor(null);
        }
    }, [selectedCardType, selectedCard, startDate, endDate]);

    // Build the where clause for server-side filtering
    const whereClause = useMemo((): TransactionFilterInput => {
        const where: TransactionFilterInput = {
            // Always filter out zero-quantity transactions
            quantity: { gt: 0 }
        };

        // Map card type to transaction type: smartcard = P, creditcard = K, bankcard = B
        const transactionTypeMap: Record<string, string> = {
            'smartcard': 'P',
            'creditcard': 'K',
            'bankcard': 'B'
        };

        // Add transaction type filter - prioritize explicit card type selection,
        // but if a specific card is selected, use its type to narrow down server-side fetching
        if (selectedCardType) {
            where.transactionType = { eq: transactionTypeMap[selectedCardType.value] };
        } else if (selectedCard?.type) {
            // Optimization: When a specific card is selected, automatically filter by its card type
            // This reduces the data fetched from server before client-side cardNumber filtering
            where.transactionType = { eq: transactionTypeMap[selectedCard.type] };
        }

        // Add date range filtering if dates are provided
        // Now handles both single dates (auto-expanded to 30 days) and date ranges
        if (startDate || endDate) {
            // Get timezone offset in milliseconds
            // NOTE: getTimezoneOffset() automatically handles DST (Daylight Saving Time)
            // - Winter (CET): UTC+1, so offset = -60 minutes
            // - Summer (CEST): UTC+2, so offset = -120 minutes
            // JavaScript automatically adjusts this based on the specific date
            const dateForOffset = startDate || endDate!;  // Use whichever date is available
            const tzOffset = dateForOffset.getTimezoneOffset() * 60 * 1000;

            // Build the at filter with gt/lt operators
            where.at = {};

            if (startDate) {
                // Create start of day by adjusting for timezone
                const startOfDay = new Date(startDate);
                startOfDay.setHours(0, 0, 0, 0);
                // Adjust to UTC by adding back the timezone offset
                const startUTC = new Date(startOfDay.getTime() + tzOffset);
                const startISO = startUTC.toISOString();

                (where.at as any).gt = startISO;  // Transactions after or on start date
            }

            if (endDate) {
                // Create end of day by adjusting for timezone
                const endOfDay = new Date(endDate);
                endOfDay.setHours(23, 59, 59, 999);
                // Adjust to UTC by adding back the timezone offset
                const endUTC = new Date(endOfDay.getTime() + tzOffset);
                const endISO = endUTC.toISOString();

                (where.at as any).lt = endISO;  // Transactions before or on end date
            }
        }

        return where;
    }, [selectedCardType, selectedCard?.type, startDate, endDate]);

    // Memoize query variables to prevent recreating on every render
    const queryVariables = useMemo(() => {
        const vars = {
            before: currentBefore,
            last: BATCH_SIZE,  // Fetch small batches due to API cost limits
            where: whereClause,
        };
        return vars;
    }, [currentBefore, whereClause]);

    // Fetch transactions
    const { data: transactionsData, loading: isLoading, error: transactionsError } = useGetCustomer<TransactionsFullCustomerDataResponse>(
        GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY,
        queryVariables,
        {
            fetchPolicy: 'network-only',
        }
    );

    // Process fetched data when it arrives
    useEffect(() => {
        if (!isLoading && transactionsData?.currentCustomer?.transactions) {
            const { pageInfo, nodes } = transactionsData.currentCustomer.transactions;

            // Skip if we already processed this cursor (prevents duplicate processing)
            // BUT: if nodes.length === 0 and both cursors are null, this is the first empty response
            // so we should NOT skip it
            const isFirstEmptyResponse = nodes.length === 0 && lastProcessedCursor === null && pageInfo.startCursor === null;

            if (!isFirstEmptyResponse && pageInfo.startCursor === lastProcessedCursor) {
                return;
            }

            // Mark this cursor as processed
            setLastProcessedCursor(pageInfo.startCursor);

            // Append new transactions to existing ones, filtering out duplicates by id
            setAllTransactions(prev => {
                // Create a Set of existing transaction IDs for fast lookup
                const existingIds = new Set(prev.map(t => t.id));

                // Filter out any transactions we already have
                const newTransactions = nodes.filter(t => !existingIds.has(t.id));

                // Track newly loaded IDs for animation (only when Load More was triggered)
                if (isLoadMoreTriggered && newTransactions.length > 0) {
                    const newIds = new Set(newTransactions.map(t => t.id));
                    setNewlyLoadedIds(newIds);

                    // Clear the newly loaded state after animation completes (4 seconds: 3.5s persist + 0.5s fade)
                    setTimeout(() => {
                        setNewlyLoadedIds(new Set());
                    }, 2500);
                }

                const updatedTransactions = [...prev, ...newTransactions];

                // Calculate filtered count to check against target
                // Card type filtering is now server-side, only specific card filtering is client-side
                let filteredForCount = updatedTransactions;

                // Apply specific card filter if selected (client-side)
                if (selectedCard) {
                    filteredForCount = filteredForCount.filter(t => {
                        // For bank cards, match by transaction type
                        if (selectedCard.type === 'bankcard') {
                            return t.transactionType === 'B';
                        }
                        // For regular cards, match by BOTH cardNumber AND transaction type
                        const expectedTransactionType = selectedCard.type === 'smartcard' ? 'P' : 'K';
                        return t.cardNumber === selectedCard.cardNumber && t.transactionType === expectedTransactionType;
                    });
                };
                const filteredCount = filteredForCount.length;

                // Check if we should continue fetching (inside the setter to use latest count)
                if (isBatchLoading) {
                    const reachedTarget = filteredCount >= TARGET_INITIAL_COUNT;
                    const noMoreData = !pageInfo.hasPreviousPage;

                    if (reachedTarget || noMoreData) {
                        setIsBatchLoading(false);
                        setIsFirstLoad(false);

                        // Scroll down only if this was triggered by "Load More" button
                        if (isLoadMoreTriggered) {
                            setTimeout(() => {
                                window.scrollBy({ top: 400, behavior: 'smooth' });
                            }, 50);
                            setIsLoadMoreTriggered(false);
                        }
                    } else {
                        setCurrentBefore(pageInfo.endCursor);  // Use endCursor to avoid duplicates
                    }
                }

                return updatedTransactions;
            });

            // Store the cursor for "Load More"
            setNextCursor(pageInfo.endCursor);

            // Check if there are more pages from API
            setHasMorePages(pageInfo.hasPreviousPage);
        } else if (!isLoading && transactionsError) {
            // Handle error state
            setIsBatchLoading(false);
            setIsFirstLoad(false);
        }
    }, [transactionsData, isLoading, lastProcessedCursor, isBatchLoading, selectedCardType, selectedCard, transactionsError]);

    // Handler for "Load More" button - fetches another batch
    const handleLoadMore = () => {
        if (nextCursor && hasMorePages) {
            setIsLoadMoreTriggered(true); // Flag to trigger scroll after loading
            setIsBatchLoading(true); // Re-enable batch loading
            setLastProcessedCursor(null); // Reset to allow processing new data
            setCurrentBefore(nextCursor);
        }
    };

    // Helper function to check if error is a 2-year API limitation error
    const is2YearLimitError = (): boolean => {
        if (!transactionsError?.message) return false;
        return transactionsError.message.includes('2 years');
    };

    // If error is 2-year limit, log it but don't display to user (warning already shown)
    if (is2YearLimitError() && transactionsError?.message) {
        console.log('API 2-year limit exceeded:', transactionsError.message);
    }

    return (
        <div className="transaction-table-list">
            {/* Initial loading state with animation */}
            {isFirstLoad && filteredTransactions.length === 0 && (
                <div className="transaction-loading"><div className="loader-square"></div> Laddar transaktioner...</div>
            )}

            {/* Progress indicator during first load - shows filtered count */}
            {isFirstLoad && filteredTransactions.length > 0 && (
                <div className="transaction-loading">
                    <div className="loader-squeeze"></div>
                    Filtrerar transaktioner...
                </div>
            )}

            {/* Error state */}
            {transactionsError && !is2YearLimitError() && (
                <div className="transaction-error">Ett fel uppstod: {transactionsError.message}</div>
            )}

            {/* Transaction count - shows filtered vs total when filter is active */}
            {!isFirstLoad && (allTransactions.length > 0 || selectedCard || selectedCardType || startDate || endDate) && (
                <div className="transaction-count">
                    {selectedCard ? (
                        <>
                            {filteredTransactions.length} {filteredTransactions.length === 1 ? 'transaktion' : 'transaktioner'} för valt kort
                            <span className="transaction-count__total"> (av {allTransactions.length} hämtade totalt)</span>
                        </>
                    ) : selectedCardType ? (
                        <>
                            {filteredTransactions.length} {selectedCardType.label.toLowerCase()}s{filteredTransactions.length === 1 ? 'transaktion' : 'transaktioner'}
                            <span className="transaction-count__total"> (av {allTransactions.length} hämtade totalt)</span>
                        </>
                    ) : (startDate || endDate) ? (
                        <>
                            {filteredTransactions.length} {filteredTransactions.length === 1 ? 'transaktion' : 'transaktioner'} för det valda datumintervallet
                            {allTransactions.length > 0 && <span className="transaction-count__total"> (av {allTransactions.length} hämtade totalt)</span>}
                        </>
                    ) : (
                        <>{filteredTransactions.length} {filteredTransactions.length === 1 ? 'transaktion hämtad' : 'transaktioner hämtade'}</>
                    )}
                    {isBatchLoading && <span className="transaction-count__loading"> (laddar fler...)</span>}
                </div>
            )}

            {/* A table header to indicate each column */}
            {!isFirstLoad && !cardsLoading && !bankCardsLoading && filteredTransactions.length > 0 && (
                <div className="transaction-table-header">
                    <span className="header-date">Datum</span>
                    <span className="header-card">Kort</span>
                    <span className="header-site">Station</span>
                    <span className="header-producttype">Produkt</span>
                    <span className="header-amount">Belopp</span>
                </div>
            )}

            {(isFirstLoad && filteredTransactions.length === 0 && cardsLoading && bankCardsLoading) || (isFirstLoad && filteredTransactions.length > 0 && cardsLoading && bankCardsLoading) && (
                <>
                    <FetchingAnimation width={'100'} height={60} delay={0} />
                    <FetchingAnimation width={'100'} height={60} delay={1} />
                    <FetchingAnimation width={'100'} height={60} delay={0} />
                    <FetchingAnimation width={'100'} height={60} delay={1} />
                    <FetchingAnimation width={'100'} height={60} delay={0} />
                </>

            )}

            {/* Transaction rows - render filtered transactions */}
            {!isFirstLoad && !cardsLoading && !bankCardsLoading && filteredTransactions.map((transaction, index) => (
                <TransactionTableSingleRow
                    key={`${transaction.id}-${index}`}
                    transaction={transaction}
                    maskedPan={getMaskedPan(transaction)}
                    productName={productLookup[transaction.productId]}
                    siteName={getSiteName(transaction.siteId)}
                    isCompany={isCompany}
                    cardRef={getCardRef(transaction)}
                    isNewlyLoaded={newlyLoadedIds.has(transaction.id)}
                    isCardDisabled={getIsCardDisabled(transaction)}
                />
            ))}

            {/* No results message - when filter yields no results but we have data */}
            {!isFirstLoad && !isBatchLoading && filteredTransactions.length === 0 && allTransactions.length > 0 && (
                <div className="transaction-no-results">
                    Inga transaktioner matchar det valda filtret.
                </div>
            )}

            {/* Empty results message - when query returned no transactions at all */}
            {!isFirstLoad && !isBatchLoading && filteredTransactions.length === 0 && allTransactions.length === 0 && (
                <div className="transaction-no-results">
                    <SearchCrossIcon width={24} height={24} />
                    Inga transaktioner hittades för det valda datumintervallet.
                </div>
            )}

            {/* Load More button - only show when there are more pages and not batch loading */}
            {hasMorePages && !isBatchLoading && filteredTransactions.length > 0 && (
                <button
                    onClick={handleLoadMore}
                    className="load-more-button Button body-text"
                    aria-label="Ladda in 10 till transaktioner"
                >
                    Ladda in fler transaktioner
                </button>
            )}

            {/* End of results message - when no more pages to load */}
            {!hasMorePages && !isBatchLoading && filteredTransactions.length > 0 && (
                <div className="transaction-end-of-list">
                    Inga fler transaktioner att hämta.
                </div>
            )}
        </div>
    )
}

export default TransactionTableList
